import { Comment, CommentCategory } from '../../types/comments'
import { 
  AlertTriangle, 
  Cloud, 
  Lightbulb, 
  HelpCircle, 
  MessageCircle 
} from 'lucide-react'

interface CommentMarkerProps {
  comment: Comment
  onClick?: (comment: Comment) => void
  isSelected?: boolean
}

const CATEGORY_CONFIG = {
  hazard: {
    icon: AlertTriangle,
    color: '#f97316', // orange-500
    bgColor: '#fed7aa', // orange-200
    label: 'Hazard'
  },
  condition: {
    icon: Cloud,
    color: '#3b82f6', // blue-500
    bgColor: '#bfdbfe', // blue-200
    label: 'Condition'
  },
  tip: {
    icon: Lightbulb,
    color: '#10b981', // emerald-500
    bgColor: '#a7f3d0', // emerald-200
    label: 'Tip'
  },
  question: {
    icon: HelpCircle,
    color: '#8b5cf6', // violet-500
    bgColor: '#ddd6fe', // violet-200
    label: 'Question'
  },
  discussion: {
    icon: MessageCircle,
    color: '#6b7280', // gray-500
    bgColor: '#e5e7eb', // gray-200
    label: 'Discussion'
  }
}

export function CommentMarker({ comment, onClick, isSelected = false }: CommentMarkerProps) {
  const config = CATEGORY_CONFIG[comment.category]
  const IconComponent = config.icon

  const handleClick = () => {
    onClick?.(comment)
  }

  const isRecent = comment.createdAt && 
    new Date().getTime() - comment.createdAt.toDate().getTime() < 24 * 60 * 60 * 1000

  return (
    <div
      className={`
        relative cursor-pointer transition-all duration-200 transform
        ${isSelected ? 'scale-110 z-10' : 'hover:scale-105'}
      `}
      onClick={handleClick}
      title={`${config.label}: ${comment.text.substring(0, 100)}${comment.text.length > 100 ? '...' : ''}`}
    >
      {/* Main marker circle */}
      <div
        className={`
          w-8 h-8 rounded-full border-2 flex items-center justify-center
          shadow-lg
          ${isSelected 
            ? 'border-white shadow-xl' 
            : 'border-gray-700 hover:border-gray-600'
          }
        `}
        style={{
          backgroundColor: config.bgColor,
          borderColor: isSelected ? '#ffffff' : config.color
        }}
      >
        <IconComponent 
          className="w-4 h-4" 
          style={{ color: config.color }}
          strokeWidth={2}
        />
      </div>

      {/* Recent indicator */}
      {isRecent && (
        <div 
          className="absolute -top-1 -right-1 w-3 h-3 rounded-full border border-white"
          style={{ backgroundColor: config.color }}
          title="Posted in the last 24 hours"
        />
      )}

      {/* Confirmation/dispute indicators */}
      {comment.confirmations && comment.confirmations.length > 0 && (
        <div 
          className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-green-500 border border-white flex items-center justify-center"
          title={`${comment.confirmations.length} user${comment.confirmations.length === 1 ? '' : 's'} confirmed this`}
        >
          <span className="text-white text-xs font-bold">✓</span>
        </div>
      )}

      {comment.disputes && comment.disputes.length > 0 && (
        <div 
          className="absolute -bottom-1 -left-1 w-4 h-4 rounded-full bg-red-500 border border-white flex items-center justify-center"
          title={`${comment.disputes.length} user${comment.disputes.length === 1 ? '' : 's'} disputed this`}
        >
          <span className="text-white text-xs font-bold">!</span>
        </div>
      )}

      {/* Resolved indicator */}
      {comment.resolved && (
        <div className="absolute inset-0 rounded-full bg-gray-500 bg-opacity-50 flex items-center justify-center">
          <span className="text-white text-xs font-bold">✓</span>
        </div>
      )}
    </div>
  )
}

export function getCommentMarkerIcon(category: CommentCategory) {
  return CATEGORY_CONFIG[category].icon
}

export function getCommentMarkerColor(category: CommentCategory) {
  return CATEGORY_CONFIG[category].color
}